/**
 * tenantCopy — tenant-specific COPY resolved from configuration, never from a
 * component literal (demo-tenant-environment Task 23, Req 2.1–2.3).
 *
 * WHAT THIS IS FOR, AND WHY IT IS SEPARATE FROM `tenantBranding`
 * -------------------------------------------------------------
 * Task 22 moved the five BRAND IDENTITY values (name, logo, two colors, header
 * text) out of component source and behind `BrandingConfig`. That covers the
 * chrome. It does not cover the long tail Req 2.2 enumerates — marketing bios,
 * affidavit text, scholarship sponsor names, mock/sample data, and the
 * timezone-explanation text that surfaces in the UI — which is prose and
 * structured data, not a five-field record, and which does not belong in the
 * `BrandingConfig` row.
 *
 * So brand identity comes from DATA (a per-tenant DynamoDB record) and tenant
 * copy comes from CONFIGURATION (this registry, keyed by tenant id). Both are
 * "not a literal embedded in a component", which is what Req 2.2 asks for.
 *
 * THE NEUTRAL FALLBACK IS THE SAME VOCABULARY TASK 22 ESTABLISHED
 * --------------------------------------------------------------
 * Req 2.3 forbids falling back to an original-client literal, so every
 * unresolved path here lands on {@link NEUTRAL_TENANT_COPY}, which is built from
 * Task 22's `NEUTRAL_BRAND_NAME`. There is deliberately ONE neutral vocabulary
 * across the branding and copy stacks — a second placeholder ("Demo Pageants",
 * "Your Organization", …) would be one more string to keep in sync and one more
 * chance for a leak to hide behind.
 *
 * IDAHO IS A REGISTRY ENTRY, NOT A DEFAULT
 * ----------------------------------------
 * Idaho's copy is still here, verbatim, so the Idaho tenant still reads as
 * Idaho — the requirement is that the literal is not BAKED INTO A COMPONENT, not
 * that Idaho's name disappears. The distinction that matters: Idaho copy is
 * reachable only by RESOLVING to the Idaho tenant. It is never what an
 * unresolved read produces. That is the whole difference between a registry and
 * a default.
 *
 * Absent copy is rendered as ABSENCE, not as invented prose: an unregistered
 * tenant gets `legacyHighlights: []` and its surface renders no tiles, exactly
 * as Task 22 renders `headerText: ''` rather than inventing a tagline.
 */

import { IDAHO_TENANT_ID } from '@/lib/application-form/activeCompetition';
import { NEUTRAL_BRAND_NAME } from '@/lib/tenantBranding';
import {
  IDAHO_TENANT_MARKETING,
  MERIDIAN_TENANT_MARKETING,
  NEUTRAL_TENANT_MARKETING,
  type TenantMarketingCopy,
} from '@/lib/tenantMarketingCopy';

/** The tenant id of the curated demo Showcase tenant (Meridian Pageants). */
export const MERIDIAN_TENANT_ID = 'tenant-meridian';

/**
 * One marketing stat tile ("40+ / Years of Legacy / <detail>").
 *
 * `detail` is the tenant's own prose. A tenant with no history to tell
 * contributes no tiles rather than a generic sentence.
 */
export interface TenantLegacyHighlight {
  /** The headline figure, e.g. `'40+'`. */
  value: string;
  /** The short label under the figure, e.g. `'Years of Legacy'`. */
  label: string;
  /** The expanded prose revealed on the tile. */
  detail: string;
}

/** The division titles a tenant awards. `mrsAlternate` is optional — most tenants have none. */
export interface TenantDivisionTitles {
  /** The married-division title, e.g. `'Mrs. Idaho America'`. */
  mrs: string;
  /** A second married-division title where the tenant runs one, e.g. `'Mrs. Idaho American'`. */
  mrsAlternate?: string;
  /** The single-division title, e.g. `'Miss Idaho for America Strong'`. */
  miss: string;
}

/**
 * Every piece of tenant-specific copy the app needs, all resolved — no
 * `undefined` holes for a component to paper over with a literal of its own
 * (the same discipline `BrandIdentity` follows).
 */
export interface TenantCopy {
  /**
   * The organization's name as it reads inside a sentence ("the {orgName}
   * organization may request documentation…").
   */
  orgName: string;
  /**
   * The phrase a division honorific attaches to: `Mrs. ${titleAuthority}`. Held
   * separately from `orgName` because the two genuinely differ — Idaho's org is
   * "Idaho America Pageants" but its titles are "Mrs. Idaho America".
   */
  titleAuthority: string;
  /** The tenant's full division title names. */
  divisionTitles: TenantDivisionTitles;
  /**
   * The IANA zone this tenant's timestamps are rendered in, so a displayed time
   * is unambiguous regardless of the viewer's locale (Req 2.2's
   * timezone-explanation text). Neutral is `UTC`: tenant-agnostic and
   * unambiguous, where `America/Boise` names the original client.
   */
  displayTimeZone: string;
  /** Marketing stat tiles. EMPTY means render none — never invented copy. */
  legacyHighlights: readonly TenantLegacyHighlight[];
  /**
   * The PUBLIC MARKETING surface's copy — team bios, organization descriptions,
   * division cards, titleholders (with their photos), and FAQ (Task 24, Req
   * 13.1–13.4). Defined in `lib/tenantMarketingCopy.ts`; hung here so there is
   * still one registry and one resolver.
   */
  marketing: TenantMarketingCopy;
  /**
   * Additional names this tenant's `BrandingConfig` record is known to carry,
   * beyond `orgName`. See {@link resolveTenantCopy} for why this exists — in
   * short, the public marketing surface resolves by NAME, and the stored record
   * does not always spell the org exactly as `orgName` does.
   *
   * An alias is one of the tenant's OWN names, so it cannot select another
   * tenant's copy any more than `orgName` can.
   */
  nameAliases?: readonly string[];
  /**
   * `true` when this record is the tenant-agnostic placeholder because no
   * tenant could be resolved (Req 2.3). Consumers use it to suppress
   * tenant-specific chrome rather than render a half-branded surface.
   */
  isNeutral: boolean;
}

/**
 * The neutral, tenant-agnostic copy. Names no tenant, so it cannot leak one.
 * Every "we don't know which tenant this is" path lands here.
 */
export const NEUTRAL_TENANT_COPY: TenantCopy = Object.freeze({
  orgName: NEUTRAL_BRAND_NAME,
  titleAuthority: NEUTRAL_BRAND_NAME,
  divisionTitles: Object.freeze({
    mrs: `Mrs. ${NEUTRAL_BRAND_NAME}`,
    miss: `Miss ${NEUTRAL_BRAND_NAME}`,
  }),
  displayTimeZone: 'UTC',
  legacyHighlights: Object.freeze([]),
  marketing: NEUTRAL_TENANT_MARKETING,
  isNeutral: true,
}) as TenantCopy;

/**
 * The founding Idaho tenant's own copy — moved here OUT of the components that
 * used to hold it. Reachable only by resolving to `tenant-idaho` (or to a
 * branding record that names Idaho), never as a fallback.
 */
const IDAHO_TENANT_COPY: TenantCopy = Object.freeze({
  orgName: 'Idaho America Pageants',
  titleAuthority: 'Idaho America',
  divisionTitles: Object.freeze({
    mrs: 'Mrs. Idaho America',
    mrsAlternate: 'Mrs. Idaho American',
    miss: 'Miss Idaho for America Strong',
  }),
  // Idaho observes Mountain Time; rendering every timestamp in one explicit zone
  // keeps the displayed value deterministic regardless of the viewer's locale.
  displayTimeZone: 'America/Boise',
  legacyHighlights: Object.freeze([
    {
      value: '40+',
      label: 'Years of Legacy',
      detail:
        'The Idaho America Pageant has been empowering women across the Gem State since 1984, making it one of the longest-running pageant programs in Idaho.',
    },
    {
      value: '14',
      label: 'National Finalists',
      detail:
        'Since 1984, Idaho has sent 8 Mrs. Idaho America finalists, 3 Mrs. Idaho American finalists, and 3 Miss Idaho for America Strong finalists to compete on the national stage.',
    },
    {
      value: '3-Year',
      label: 'Costume Streak',
      detail:
        'For three consecutive years, at least one of our titleholders has brought home a national costume award at the Mrs. America Competition — an area where Idaho consistently shines.',
    },
    {
      value: '1',
      label: 'Mrs. World',
      detail:
        'In 2013, Mrs. Idaho America Kaley Sparling went on to win the title of Mrs. World — the highest title achievable in our pageant system.',
    },
  ]),
  marketing: IDAHO_TENANT_MARKETING,
  // The names Idaho's own BrandingConfig record and chrome are known to use.
  // `seed.ts` / `buildSeedDataset.ts` write `pageantName: 'Idaho America Pageant'`
  // (singular), and the nav/footer wordmark has always read "Idaho Pageants" —
  // neither is `orgName`, and the public marketing site has no tenant claim to
  // fall back on. Without these, Idaho's own site would fail to resolve its own
  // copy and render the neutral empty state.
  nameAliases: Object.freeze([
    'Idaho America Pageant',
    'Idaho Pageants',
    'Idaho Pageant',
    'Mrs. Idaho America Pageants',
    'Mrs. Idaho America Pageant',
  ]),
  isNeutral: false,
}) as TenantCopy;

/**
 * The demo Showcase tenant's copy.
 *
 * `legacyHighlights` is intentionally EMPTY: those tiles state a multi-decade
 * competition record, and fabricating one is the curated-dataset work (Task 31,
 * which also produces the AI-generated headshots Req 11.5 requires). An empty
 * list renders no tiles, which is honest. Meridian's ORGANIZATION-level marketing
 * prose does exist — see `MERIDIAN_TENANT_MARKETING` — so the demo's public site
 * reads as a real Meridian org rather than a blank shell. What matters for Req
 * 2.1 is that nothing here contains a member of the variant set.
 */
const MERIDIAN_TENANT_COPY: TenantCopy = Object.freeze({
  orgName: 'Meridian Pageants',
  titleAuthority: 'Meridian',
  divisionTitles: Object.freeze({
    mrs: 'Mrs. Meridian',
    miss: 'Miss Meridian',
  }),
  displayTimeZone: 'America/Denver',
  legacyHighlights: Object.freeze([]),
  marketing: MERIDIAN_TENANT_MARKETING,
  nameAliases: Object.freeze(['Meridian Pageant']),
  isNeutral: false,
}) as TenantCopy;

/**
 * Tenant copy keyed by tenant id — the "configuration or tenant data keyed to
 * the active tenant identifier" of Req 2.2. Adding a tenant is a registry entry,
 * not a component edit.
 */
export const TENANT_COPY_BY_ID: Readonly<Record<string, TenantCopy>> = Object.freeze({
  [IDAHO_TENANT_ID]: IDAHO_TENANT_COPY,
  [MERIDIAN_TENANT_ID]: MERIDIAN_TENANT_COPY,
});

/** Collapse whitespace and case so a name comparison is not defeated by formatting. */
function normalizeName(value: string | null | undefined): string {
  return (typeof value === 'string' ? value : '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * `orgName` (and each tenant's own `nameAliases`) → copy, for the brand-name
 * lookup below. Derived from the registry, so it cannot drift out of sync with it.
 *
 * The FIRST entry for a given normalized name wins, so a tenant can never have
 * its `orgName` displaced by another tenant's alias — a collision keeps whichever
 * tenant claimed the name first and is caught by the registry test.
 */
const COPY_BY_ORG_NAME: ReadonlyMap<string, TenantCopy> = (() => {
  const byName = new Map<string, TenantCopy>();
  for (const copy of Object.values(TENANT_COPY_BY_ID)) {
    for (const name of [copy.orgName, ...(copy.nameAliases ?? [])]) {
      const key = normalizeName(name);
      if (key && !byName.has(key)) byName.set(key, copy);
    }
  }
  return byName;
})();

/** Inputs to tenant-copy resolution. */
export interface ResolveTenantCopyInput {
  /** The verified active-tenant claim (`AuthUser.activeTenantId`), if any. */
  activeTenantId?: string | null;
  /**
   * The already-resolved brand name from `BrandingContext` (`identity.name`).
   * Used as the SECOND resolution signal — see {@link resolveTenantCopy}.
   */
  brandName?: string | null;
}

/**
 * Resolve the active tenant's copy (Req 2.2), falling back to neutral and never
 * to an original-client literal (Req 2.3).
 *
 * Precedence:
 *   1. The `activeTenantId` claim against the registry. The strongest signal —
 *      a verified claim naming a tenant we have copy for.
 *   2. The resolved brand name against the registry — the tenant's `orgName` OR
 *      any of its own `nameAliases` — matched case-insensitively after collapsing
 *      whitespace. This exists for the PUBLIC / pre-auth surface, which carries
 *      no tenant claim at all: there the year-keyed `BrandingConfig.pageantName`
 *      is the only tenant signal available, and it is what keeps Idaho's own
 *      public site reading as Idaho. This cannot leak, because it only reaches a
 *      tenant's copy when the DATA already names that tenant — a record saying
 *      "Meridian Pageants" can never select Idaho's.
 *
 *      The ALIASES matter more than they look (Task 24). Idaho's stored record
 *      says `'Idaho America Pageant'` (singular — see `seed.ts`) while its
 *      `orgName` is `'Idaho America Pageants'`, and its chrome wordmark has always
 *      read `'Idaho Pageants'`. Exact-`orgName` matching would therefore MISS on
 *      Idaho's own live site, and once the marketing prose moved into this
 *      registry that miss would blank Idaho's public About pages. Every alias is
 *      one of the tenant's own names, so widening the match widens nothing but
 *      that tenant's own reachability.
 *   3. Neutral copy, specialized with the resolved brand name where there is
 *      one. That specialization mirrors Task 22's deliberate exception: a
 *      Meridian prospect whose copy is unregistered still sees their OWN
 *      tenant's name rather than a generic placeholder, and can never see
 *      another tenant's. `isNeutral` stays `true` because the copy itself is
 *      still unresolved.
 */
export function resolveTenantCopy(input: ResolveTenantCopyInput = {}): TenantCopy {
  const id = typeof input.activeTenantId === 'string' ? input.activeTenantId.trim() : '';
  if (id) {
    const byId = TENANT_COPY_BY_ID[id];
    if (byId) return byId;
  }

  const brandName = typeof input.brandName === 'string' ? input.brandName.trim() : '';
  const byName = COPY_BY_ORG_NAME.get(normalizeName(brandName));
  if (byName) return byName;

  if (!brandName || normalizeName(brandName) === normalizeName(NEUTRAL_BRAND_NAME)) {
    return NEUTRAL_TENANT_COPY;
  }

  return {
    ...NEUTRAL_TENANT_COPY,
    orgName: brandName,
    titleAuthority: brandName,
    divisionTitles: { mrs: `Mrs. ${brandName}`, miss: `Miss ${brandName}` },
    isNeutral: true,
  };
}

/**
 * The full title for a division, e.g. `'Mrs. Idaho America'`.
 *
 * Takes the same `'MRS' | 'MISS'` discriminator the `ApplicantYearProfile`
 * carries, so callers pass `profile.applicantType` straight through instead of
 * re-deriving an honorific and gluing a tenant name onto it.
 */
export function divisionTitle(copy: TenantCopy, applicantType: string | null | undefined): string {
  return applicantType === 'MRS' ? copy.divisionTitles.mrs : copy.divisionTitles.miss;
}

/** The honorific alone (`'Mrs.'` / `'Miss'`), for surfaces that render it separately. */
export function divisionHonorific(applicantType: string | null | undefined): string {
  return applicantType === 'MRS' ? 'Mrs.' : 'Miss';
}

/**
 * The tenant's division titles as an English list ("A, B, and C"), used by the
 * affidavit intro. Skips `mrsAlternate` when the tenant has none, so a
 * two-division tenant does not read as though it has three.
 */
export function divisionTitleList(copy: TenantCopy): string {
  const titles = [copy.divisionTitles.mrs, copy.divisionTitles.mrsAlternate, copy.divisionTitles.miss]
    .filter((t): t is string => typeof t === 'string' && t.trim().length > 0);
  if (titles.length <= 1) return titles[0] ?? copy.orgName;
  if (titles.length === 2) return `${titles[0]} and ${titles[1]}`;
  return `${titles.slice(0, -1).join(', ')}, and ${titles[titles.length - 1]}`;
}
